import { customAlphabet } from "nanoid"
import type { Cart } from "./types"

const BASE52 = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz"
const BASE36 = "0123456789ACDEFGHJKLMNPQRTUVWXYZ"

/**
 * Generates a unique identifier for internal use
 * @returns A unique string identifier
 */
export const generateUniqueId = (): string => {
  return customAlphabet(BASE52, 24)()
}

/**
 * Generates a user-facing cart ID
 * @returns A shorter, user-friendly identifier
 */
export const generateUserFacingId = (): string => {
  return customAlphabet(BASE36, 8)()
}

/**
 * Checks if a SKU matches the gift card SKU pattern (e.g., SWATGIFT-50)
 * This is a low-level check for SKU strings only.
 *
 * @param sku - The SKU string to check
 * @returns True if the SKU starts with "SWATGIFT" (case-insensitive)
 */
export const isGiftCardSku = (sku: string | null | undefined): boolean => {
  return typeof sku === "string" && sku.toUpperCase().startsWith("SWATGIFT")
}

/**
 * @deprecated Use isGiftCardSku instead. Kept for backwards compatibility.
 */
export const isSwatgiftSku = isGiftCardSku

/**
 * Checks if an item is a gift card (digital product)
 * This is the unified method that checks both the isGiftCard flag and SKU pattern.
 *
 * @param item - An object with optional isGiftCard flag and/or sku string
 * @returns True if the item is a gift card (by flag or SKU pattern)
 */
export const isGiftCardItem = (item: {
  isGiftCard?: boolean
  sku?: string | null
}): boolean => {
  return item.isGiftCard === true || isGiftCardSku(item.sku)
}

/**
 * Checks if all items in the cart are gift cards (digital products)
 * Gift cards don't require shipping and are not taxable
 *
 * @param items - Cart items to check
 * @returns True if all items are gift cards, false otherwise
 */
export const areAllItemsGiftCards = (items: Cart.Item[]): boolean => {
  if (!items || items.length === 0) return false
  return items.every(isGiftCardItem)
}

/**
 * Checks if any item in the cart is a pre-order item
 * Checks for both null and undefined explicitly, since preOrder may be
 * undefined from localStorage deserialization or partial data.
 *
 * @param items - Cart items to check
 * @returns True if any item has a preOrder date set
 */
export const hasPreOrderItems = (items: Cart.Item[]): boolean => {
  if (!items || items.length === 0) return false
  return items.some(
    (item) => item.preOrder !== null && item.preOrder !== undefined
  )
}

/**
 * Normalizes a cart item's `discountPrice` so a $0 line can never enter the
 * cart. When `discountPrice` is falsy (0, NaN, missing), falls back to the
 * regular `price`. Applied recursively to a bundle's `bundleItems[]`.
 *
 * Used at every cart write boundary — `addItem`, `replaceItem`, and on
 * `loadCartData` so historical $0 lines persisted from before this fix
 * cannot re-hydrate into the live cart.
 *
 * @param item - A single or bundle cart item that may carry a falsy discountPrice
 * @returns The same item with discountPrice (and bundle children) normalized
 */
export const withFinalDiscountPrice = (item: Cart.Item): Cart.Item => {
  const final = (p: { price: number; discountPrice: number }) =>
    p.discountPrice > 0 ? p.discountPrice : p.price

  if (item.type === "bundle") {
    return {
      ...item,
      discountPrice: final(item),
      bundleItems: item.bundleItems.map((b) => ({
        ...b,
        discountPrice: final(b),
      })),
    }
  }

  return {
    ...item,
    discountPrice: final(item),
  }
}
